import { useMemo, useState } from 'react'
import { Card } from '../../../components/ui/Card'
import { Field } from '../../../components/ui/Field'
import { Button } from '../../../components/ui/Button'
import type { EfsaIngredient, Eticheta, IngredientItem, BeneficiuItem, Nivel } from './types'
import {
  PICTOGRAME,
  FORME,
  MADE_IN_OPTIONS,
  AUTORITATI,
  DIM_PRESETS,
  NIVEL_DOTS,
  autoritateCorecta,
} from './types'
import { checkDozaVsUL, validateBeneficiu } from './validation'

const PICTOGRAME_LABELS: Record<string, string> = {
  VEGAN: 'VEGAN',
  VEGETARIAN: 'VEGETARIAN',
  FARA_GLUTEN: 'FĂRĂ GLUTEN',
  FARA_LACTOZA: 'FĂRĂ LACTOZĂ',
  FARA_ZAHAR: 'FĂRĂ ZAHĂR',
  FARA_AROME: 'FĂRĂ AROME',
}

interface Props {
  draft: Partial<Eticheta>
  setDraft: (e: Partial<Eticheta>) => void
  ingrediente: EfsaIngredient[]
  onSave: () => void
  saving: boolean
  isEdit: boolean
  onPreview: () => void
}

export function GeneratorTab({
  draft,
  setDraft,
  ingrediente,
  onSave,
  saving,
  isEdit,
  onPreview,
}: Props) {
  const u = (patch: Partial<Eticheta>) => setDraft({ ...draft, ...patch })

  const ingrSelected = (draft.ingrediente ?? []) as IngredientItem[]
  const beneficii = (draft.beneficii ?? []) as BeneficiuItem[]

  const efsaMap = useMemo(() => {
    const m = new Map<string, EfsaIngredient>()
    for (const i of ingrediente) m.set(i.key, i)
    return m
  }, [ingrediente])

  const addIngredient = (key: string) => {
    if (ingrSelected.some((i) => i.key === key)) return
    if (ingrSelected.length >= 5) return
    const ef = efsaMap.get(key)
    if (!ef) return
    u({
      ingrediente: [
        ...ingrSelected,
        {
          key: ef.key,
          label: ef.label,
          doza: '',
          mentiune: ef.mentiune,
          nivel: ef.nivel as Nivel,
        },
      ],
    })
  }

  const removeIngredient = (key: string) => {
    u({ ingrediente: ingrSelected.filter((i) => i.key !== key) })
  }

  const updateIngredient = (key: string, patch: Partial<IngredientItem>) => {
    u({
      ingrediente: ingrSelected.map((i) => (i.key === key ? { ...i, ...patch } : i)),
    })
  }

  const togglePicto = (p: string) => {
    const cur = draft.pictograme ?? []
    if (cur.includes(p)) u({ pictograme: cur.filter((x) => x !== p) })
    else u({ pictograme: [...cur, p] })
  }

  const addBeneficiu = () => {
    u({ beneficii: [...beneficii, { text: '', status: 'gol' }] })
  }
  const updateBeneficiu = (idx: number, text: string) => {
    const status = validateBeneficiu(text, ingrSelected)
    u({
      beneficii: beneficii.map((b, i) => (i === idx ? { text, status } : b)),
    })
  }
  const removeBeneficiu = (idx: number) => {
    u({ beneficii: beneficii.filter((_, i) => i !== idx) })
  }

  const [madeInCustom, setMadeInCustom] = useState(
    draft.madeIn && !MADE_IN_OPTIONS.some((m) => m.value === draft.madeIn) ? draft.madeIn : '',
  )
  const madeInValue = MADE_IN_OPTIONS.some((m) => m.value === draft.madeIn)
    ? (draft.madeIn ?? '')
    : 'custom'

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* === COLOANA STANGA: informatii produs === */}
      <Card title="Informații produs (fața etichetei)">
        <div className="space-y-3">
          <Field
            label="Denumire produs (trebuie să conțină „PREMIUM”)"
            value={draft.denumire ?? ''}
            onChange={(e) => u({ denumire: e.target.value })}
            placeholder="ex: Premium Zinc Bisglycinate"
          />
          <Field
            label="Subtitlu față"
            value={draft.subtitlu ?? ''}
            onChange={(e) => u({ subtitlu: e.target.value })}
            placeholder="SUPLIMENT ALIMENTAR CU ZINC"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Nr. capsule / porții"
              value={draft.portii ?? ''}
              onChange={(e) => u({ portii: e.target.value })}
              placeholder="60 CAPSULE"
            />
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-dim">
                Forma produsului
              </label>
              <select
                value={draft.forma ?? ''}
                onChange={(e) => u({ forma: e.target.value })}
                className="w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— alege —</option>
                {FORME.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Field
            label="Cantitate netă"
            value={draft.gramaj ?? ''}
            onChange={(e) => u({ gramaj: e.target.value })}
            placeholder="33 g"
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-dim">
              Țara de origine (față)
            </label>
            <select
              value={madeInValue}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  u({ madeIn: madeInCustom || '' })
                } else {
                  u({ madeIn: e.target.value })
                }
              }}
              className="w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              {MADE_IN_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            {madeInValue === 'custom' && (
              <input
                value={madeInCustom}
                onChange={(e) => {
                  setMadeInCustom(e.target.value)
                  u({ madeIn: e.target.value })
                }}
                placeholder="MADE IN ROMANIA / CZECH / ..."
                className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
              />
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-dim">Pictograme</label>
            <div className="flex flex-wrap gap-2">
              {PICTOGRAME.map((p) => {
                const checked = (draft.pictograme ?? []).includes(p)
                return (
                  <label
                    key={p}
                    className={`cursor-pointer rounded border px-2 py-1 text-xs ${
                      checked ? 'border-b4l-blue-dark bg-b4l-blue-light/40' : 'border-b4l-border'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePicto(p)}
                      className="mr-1.5"
                    />
                    {PICTOGRAME_LABELS[p] ?? p}
                  </label>
                )
              })}
            </div>
          </div>
          <DimensionPicker
            w={draft.dimW ?? null}
            h={draft.dimH ?? null}
            onChange={(w, h) => u({ dimW: w, dimH: h })}
          />
        </div>
      </Card>

      {/* === COLOANA DREAPTA: ingrediente + beneficii + producator === */}
      <div className="space-y-4">
        <Card title="Ingrediente active EFSA (max 5)">
          <IngredientSearchAdd
            ingrediente={ingrediente}
            selectedKeys={ingrSelected.map((i) => i.key)}
            onAdd={addIngredient}
            disabled={ingrSelected.length >= 5}
          />
          {ingrSelected.length === 0 && (
            <p className="mt-3 text-xs text-b4l-text-muted">
              Niciun ingredient adăugat. Caută un ingredient EFSA și adaugă-l în listă.
            </p>
          )}
          <div className="mt-3 space-y-2">
            {ingrSelected.map((i) => {
              const ef = efsaMap.get(i.key)
              const ulCheck = ef ? checkDozaVsUL(ef, i.doza) : null
              return (
                <div key={i.key} className="rounded border border-b4l-border p-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full ${NIVEL_DOTS[i.nivel]}`}
                    />
                    <span className="flex-1 text-sm font-medium">{i.label}</span>
                    <button
                      onClick={() => removeIngredient(i.key)}
                      className="text-xs text-b4l-text-muted hover:text-b4l-red-dark"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      value={i.doza}
                      onChange={(e) => updateIngredient(i.key, { doza: e.target.value })}
                      placeholder="ex: 15 mg"
                      className="rounded border border-b4l-border px-2 py-1 text-xs"
                    />
                    <div className="text-[10px] text-b4l-text-muted">
                      {ef?.ul ? `UL: ${ef.ul} ${ef.ulUnit}/zi` : 'fără UL'}
                    </div>
                  </div>
                  {ulCheck && (
                    <div
                      className={`mt-1 rounded px-2 py-1 text-[11px] ${
                        ulCheck.nivel === 'rosu'
                          ? 'bg-b4l-red-light text-b4l-red-dark'
                          : ulCheck.nivel === 'portocaliu'
                            ? 'bg-b4l-amber-light text-b4l-amber-dark'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {ulCheck.mesaj}
                    </div>
                  )}
                  <textarea
                    value={i.mentiune}
                    onChange={(e) => updateIngredient(i.key, { mentiune: e.target.value })}
                    rows={2}
                    placeholder="Mențiune EFSA (precompletată, editabilă)"
                    className="mt-2 w-full rounded border border-b4l-border px-2 py-1 text-xs"
                  />
                </div>
              )
            })}
          </div>
        </Card>

        <Card title="Mențiune EFSA principală">
          <textarea
            value={draft.mentiune ?? ''}
            onChange={(e) => u({ mentiune: e.target.value })}
            rows={3}
            placeholder="ex: „Zincul contribuie la menținerea funcției normale a sistemului imunitar”"
            className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </Card>

        <Card title="Beneficii produs">
          {beneficii.map((b, idx) => (
            <div key={idx} className="mb-2 flex items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                  b.status === 'verde'
                    ? 'bg-b4l-green'
                    : b.status === 'portocaliu'
                      ? 'bg-b4l-amber'
                      : b.status === 'rosu'
                        ? 'bg-b4l-red'
                        : 'bg-b4l-text-muted'
                }`}
              />
              <input
                value={b.text}
                onChange={(e) => updateBeneficiu(idx, e.target.value)}
                placeholder="ex: Susține sistemul imunitar…"
                className="flex-1 rounded border border-b4l-border px-2 py-1 text-sm"
              />
              <button
                onClick={() => removeBeneficiu(idx)}
                className="text-xs text-b4l-text-muted hover:text-b4l-red-dark"
              >
                ✕
              </button>
            </div>
          ))}
          <Button variant="secondary" onClick={addBeneficiu} className="!px-2 !py-1 !text-xs">
            + Beneficiu
          </Button>
        </Card>

        <Card title="Compoziție / mod de utilizare">
          <div className="space-y-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-dim">
                Tabel ingrediente per porție (text liber)
              </label>
              <textarea
                value={draft.compozitie ?? ''}
                onChange={(e) => u({ compozitie: e.target.value })}
                rows={3}
                placeholder="Per 1 cps: Zinc bisglicinat 75mg (echiv. 15mg Zn elementar = 150% VNR*)"
                className="w-full rounded border border-b4l-border px-2 py-1.5 font-mono text-xs"
              />
            </div>
            <Field
              label="Mod de utilizare adulți"
              value={draft.utilizareAdulti ?? ''}
              onChange={(e) => u({ utilizareAdulti: e.target.value })}
              placeholder="1 capsulă pe zi cu apă, sau la recomandarea specialistului"
            />
            <Field
              label="Mod de utilizare copii (opțional)"
              value={draft.utilizareCopii ?? ''}
              onChange={(e) => u({ utilizareCopii: e.target.value })}
            />
          </div>
        </Card>

        <Card title="Date producător + notificare">
          <div className="space-y-2">
            <Field
              label="Țara de producție + firma"
              value={draft.tara ?? ''}
              onChange={(e) => u({ tara: e.target.value })}
              placeholder="Produs în Germania pentru Divers SEO Market S.R.L."
            />
            <Field
              label="Adresă"
              value={draft.adresa ?? ''}
              onChange={(e) => u({ adresa: e.target.value })}
              placeholder="Șos. Doamna Ghica nr. 187, Sector 2, București, CP 022826"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-b4l-text-dim">
                  Autoritate
                </label>
                <select
                  value={autoritateCorecta(draft.autoritate) ?? ''}
                  onChange={(e) => u({ autoritate: e.target.value })}
                  className="w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">— alege —</option>
                  {AUTORITATI.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Nr. notificare"
                value={draft.notificare ?? ''}
                onChange={(e) => u({ notificare: e.target.value })}
                placeholder="nr. 03 / 28.05.2025"
              />
            </div>
            <Field
              label="Deținător notificare"
              value={draft.detinator ?? ''}
              onChange={(e) => u({ detinator: e.target.value })}
              placeholder="DIVERS SEO MARKET S.R.L."
            />
            <Field
              label="EAN (13 cifre, prefix B4L: 5944759)"
              value={draft.ean ?? ''}
              onChange={(e) => u({ ean: e.target.value })}
              placeholder="5944759000000"
            />
            <Field
              label="Avertizare specifică produsului"
              value={draft.avertSpecific ?? ''}
              onChange={(e) => u({ avertSpecific: e.target.value })}
              placeholder="ex: A nu se consuma de către copiii sub 16 ani."
            />
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onPreview}>
            Previzualizează →
          </Button>
          <Button onClick={onSave} loading={saving}>
            {isEdit ? 'Salvează' : 'Generează eticheta'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function DimensionPicker({
  w,
  h,
  onChange,
}: {
  w: number | null
  h: number | null
  onChange: (w: number | null, h: number | null) => void
}) {
  const matched = DIM_PRESETS.findIndex((p) => p.w === w && p.h === h)
  const [presetIdx, setPresetIdx] = useState<number>(matched >= 0 ? matched : -1)
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-b4l-text-dim">
        Dimensiune etichetă
      </label>
      <select
        value={presetIdx}
        onChange={(e) => {
          const idx = Number(e.target.value)
          setPresetIdx(idx)
          if (idx >= 0) {
            const p = DIM_PRESETS[idx]
            onChange(p.w, p.h)
          }
        }}
        className="mb-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
      >
        <option value={-1}>Personalizat (manual)</option>
        {DIM_PRESETS.map((p, i) => (
          <option key={i} value={i}>
            {p.label}
          </option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          placeholder="Lățime (mm)"
          value={w ?? ''}
          onChange={(e) => {
            setPresetIdx(-1)
            onChange(e.target.value ? Number(e.target.value) : null, h)
          }}
          className="rounded border border-b4l-border px-2 py-1 text-sm"
        />
        <input
          type="number"
          placeholder="Înălțime (mm)"
          value={h ?? ''}
          onChange={(e) => {
            setPresetIdx(-1)
            onChange(w, e.target.value ? Number(e.target.value) : null)
          }}
          className="rounded border border-b4l-border px-2 py-1 text-sm"
        />
      </div>
    </div>
  )
}

function IngredientSearchAdd({
  ingrediente,
  selectedKeys,
  onAdd,
  disabled,
}: {
  ingrediente: EfsaIngredient[]
  selectedKeys: string[]
  onAdd: (key: string) => void
  disabled: boolean
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = ingrediente.filter((i) => !selectedKeys.includes(i.key))
    if (!q) return list.slice(0, 50)
    return list
      .filter((i) => i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q))
      .slice(0, 50)
  }, [ingrediente, search, selectedKeys])

  const remaining = 5 - selectedKeys.length

  return (
    <div>
      <div className="mb-1 text-[11px] text-b4l-text-muted">
        {disabled
          ? 'Maxim 5 ingrediente atinse — șterge unul ca să adaugi altul'
          : `Mai poți adăuga ${remaining} ${remaining === 1 ? 'ingredient' : 'ingrediente'}. Scrie câteva litere și alege din listă.`}
      </div>
      <div className="flex gap-2">
        <input
          value={search}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          disabled={disabled}
          placeholder={
            disabled
              ? 'Maxim 5 ingrediente atinse'
              : '🔍 Caută ingredient (zinc, vitD3, ashwagandha)…'
          }
          className="flex-1 rounded border border-b4l-border px-2 py-1.5 text-sm disabled:bg-b4l-bg"
        />
        {open && (
          <button
            onClick={() => setOpen(false)}
            className="text-xs text-b4l-text-muted hover:text-b4l-text"
          >
            Închide
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="mt-1 max-h-56 overflow-y-auto rounded border border-b4l-border bg-white">
          {filtered.length === 0 ? (
            <div className="p-2 text-center text-xs text-b4l-text-muted">Nimic găsit</div>
          ) : (
            filtered.map((i) => (
              <button
                key={i.key}
                onClick={() => {
                  onAdd(i.key)
                  setSearch('')
                  // Lista ramane deschisa => poti adauga consecutiv pana atingi limita
                }}
                className="flex w-full items-center gap-2 border-b border-b4l-border px-2 py-1.5 text-left text-xs last:border-0 hover:bg-b4l-bg-soft"
              >
                <span
                  className={`inline-block h-2 w-2 shrink-0 rounded-full ${NIVEL_DOTS[i.nivel]}`}
                />
                <span className="flex-1 truncate">{i.label}</span>
                <span className="text-[10px] text-b4l-text-muted">{i.grupa}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
